import { carrierEntryDefinition } from './carrier-entry.js';
import { withAsyncDeadline } from '../../shared/async-deadline.js';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
// Union of the force, walk and fall scanners (fall additionally reads feats).
const CARRIERS = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse', 'at_will_spell',
  'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability', 'feat'];

/** Fill only cold status/carrier definitions; the held roster and compiled actors stay intact.
 * Recheck on each warmup: a later member/grant delta can introduce a new pointer
 * after the encounter's original status/prop preload has already settled. */
export function hydrateMovementCarrierDefinitions(encounter: Row,
  read: { entries(row: Row, key: string): unknown[]; statuses(row: Row): Row[]; definition(id: string): Row | null },
  fetch: (id: string) => Promise<unknown>): Promise<void> | null {
  const missing = new Set<string>();
  for (const value of Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : []) {
    const row = record(value);
    if (!row) continue;
    const entries: unknown[] = [...read.statuses(row)];
    for (const key of CARRIERS) entries.push(...read.entries(row, key), ...(Array.isArray(row[key]) ? row[key] : []));
    for (const entry of entries) {
      const resolved = carrierEntryDefinition(entry, read.definition);
      if (!resolved.definition && /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i.test(resolved.id)) missing.add(resolved.id.toUpperCase());
    }
  }
  return missing.size ? Promise.all([...missing].map(id => withAsyncDeadline(async signal => {
    const definition = record(await fetch(id));
    // A parked definition/adoption must release the move even when the fetch
    // ignores cancellation. A late answer cannot publish this expired warmup.
    signal.throwIfAborted();
    // Missing/failed reads stay absent, so the resolvers retain the exact cause.
    if (String(record(definition?.items)?.item_id ?? '').toUpperCase() !== id) return;
    const catalog = record(encounter.catalog) ?? (encounter.catalog = {} as Row);
    catalog[id] = definition;
  }, 20000).catch(() => undefined))).then(() => undefined) : null;
}
